import { Capacitor } from "@capacitor/core";
import type { AxiosResponse } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnniePanel } from "../../components/Annie/AnniePanel";
import { AnnieWalkthroughController } from "../../components/Annie/AnnieWalkthroughController";
import { ContentWrapper } from "../../components/contentWrapper";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { Header } from "../../components/header";
import { ProductionEnvBanner } from "../../components/header/ProductionEnvBanner";
import { PageContainer } from "../../components/pageContainer";
import { GapsBottomNav } from "../../components/staffing/mobileGaps/GapsBottomNav";
import { useIsGapsViewActive } from "../../components/staffing/mobileGaps/useGapsViewPreference";
import { NO_RUNNING_WALKTHROUGH_ANCHORS, WalkthroughRunningContext } from "../../components/UIToolkit/Walkthrough/walkthroughRunning";
import { UserGuideChatWidget } from "../../components/UserGuideChatWidget/UserGuideChatWidget";
import { AnnieAskProvider } from "../../contexts/AnnieAskProvider";
import { type Department, type Token, useAuth } from "../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useIsMobile } from "../../hooks/useMobile";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useViewMode } from "../../hooks/useViewMode";
import { localStorageService } from "../../services/LocalStorageService";
import { readRefreshToken } from "../../services/storageService.service";
import { BOTTOM_NAV } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import type { AnnieWalkthrough } from "../../utils/annieStream";
import { CAREERS_PATH } from "../../utils/careersRoutes";
import { isHiringTabHiddenForDepartment, NurseRole } from "../../utils/models";
import axiosWrapper from "../../utils/networkWrapper";
import { resolveDepartmentSwitchDestination } from "../../utils/staffingRoutes";
import { departmentsForCurrentView, isBedSideNurse, isHeadNurse } from "../../utils/utilFunctions";
import { useCareersAccess } from "../Careers/hooks/useCareersAccess";
import { useHubTabAccessQuery } from "../HiringHub/hooks/useHiringHub";
import Styles from "./Layout.module.scss";

const tabs_priority = ["/shift-insights", "/staffing", "/command-center", "/planning", "/team", "/availability", "/404"] as const satisfies readonly string[];

const tabs_to_feature_flag = {
	"/shift-insights": "shift_insights_tab",
	"/staffing": "staffing_tab",
	"/command-center": "command_center_tab",
	"/team": "team_tab",
	"/availability": "availability_tab",
	"/facilities": "facilities_tab",
	"pbj-calculator": "pbj_calculator_tab",
	"pbj-model": "pbj_model",
	"/404": "",
} as const satisfies Record<string, string>;

const tabs_name_map = {
	"Shift Insights": "/shift-insights",
	"Workforce Planning": "/planning",
	"Update Staffing": "/staffing",
	"Command Center": "/command-center",
	Team: "/team",
	"Schedule Template": "/master-schedule",
	Availability: "/availability",
	"Management Center": "/management-center",
	Patients: "/patients",
	Facilities: "/facilities",
	"ROI Calculator": "/pbj-calculator",
	"PBJ Model": "/pbj-model",
	"Ops Backoffice": "/ops-backoffice",
	Hiring: "/hiring-chat",
	Careers: CAREERS_PATH,
} as const satisfies Record<string, string>;
type TabName = keyof typeof tabs_name_map;

/**
 * Pages a tab owns beyond its own route. The day schedule is reached from the staffing grid, and also
 * straight from a link (the wage breakdown's "Back to wage breakdown", a notification); without this the
 * header kept highlighting whichever tab was last active, e.g. Team.
 */
const TAB_PATH_ALIASES: Partial<Record<TabName, readonly string[]>> = {
	"Update Staffing": ["/day-schedule"],
};

// DEFAULT: a tab with no aliases owns only its own route.
const isTabPath = (tabName: TabName, path: string): boolean =>
	[tabs_name_map[tabName], ...(TAB_PATH_ALIASES[tabName] ?? [])].some((tabPath) => path.includes(tabPath));

/** `activeTab` when no header tab owns the route: nothing is highlighted (`findIndex`'s own "none"). */
const NO_ACTIVE_TAB = -1;

const DEPARTMENT_INDEPENDENT_ROUTES = ["/ops-backoffice", "/hiring-chat", CAREERS_PATH] as const satisfies readonly (typeof tabs_name_map)[TabName][];

export function Layout() {
	// An index into the header's tabs, or `NO_ACTIVE_TAB` while the route is none of theirs (a profile, Settings).
	const [activeTab, setActiveTab] = useState(NO_ACTIVE_TAB);
	const [activeWalkthrough, setActiveWalkthrough] = useState<AnnieWalkthrough | null>(null);
	const [isWalkthroughRunning, setIsWalkthroughRunning] = useState(false);
	// The running tour's anchor names, for a control that shows itself only while a tour spotlights it.
	// Gated on the controller's RUNNING report, not on the request: a refused tour never spotlights anything.
	// The resolved steps are a superset of what renders; a step dropped at launch has no control on the page.
	const runningWalkthroughAnchors = useMemo<ReadonlySet<string>>(
		() => (isWalkthroughRunning && activeWalkthrough !== null ? new Set(activeWalkthrough.steps.map((step) => step.anchor)) : NO_RUNNING_WALKTHROUGH_ANCHORS),
		[isWalkthroughRunning, activeWalkthrough],
	);
	// Stable identity: the controller takes this as an effect dependency.
	const clearWalkthrough = useCallback(() => setActiveWalkthrough(null), []);
	// Annie's open state, owned here for BOTH layouts so it survives a viewport flip (see the mount below).
	const [isAnnieOpen, setIsAnnieOpen] = useState(false);
	const { userInfo } = useAuth();
	const { isMobile } = useIsMobile();
	const { isAnyDemo, chosenDepartment, isHospital } = useHospitalData();
	const location = useLocation();
	const currentPath = location.pathname;
	const navigate = useNavigate();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const { view } = useViewMode();

	// Only departments valid for the current view — the switch sends `view`, so the rest would 403.
	const departmentsForView = useMemo(
		() => departmentsForCurrentView(userInfo?.departments, view, userInfo.adminDepartmentIds, userInfo.staffDepartmentIds),
		[userInfo?.departments, userInfo.adminDepartmentIds, userInfo.staffDepartmentIds, view],
	);

	const isShiftInsightsEnabled = useFeatureFlag(FEATURE_FLAGS.ShiftInsightsTab);
	const isStaffingEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingTab, true);
	const isCommandCenterEnabled = useFeatureFlag(FEATURE_FLAGS.CommandCenterTab);
	const isTeamEnabled = useFeatureFlag(FEATURE_FLAGS.TeamTab);
	const isMasterScheduleEnabled = useFeatureFlag(FEATURE_FLAGS.MasterScheduleTab);
	const isManagementCenterEnabled = useFeatureFlag(FEATURE_FLAGS.ManagementCenterTab);
	const isUserGuideChatEnabled = useFeatureFlag(FEATURE_FLAGS.UserGuideChat);
	const isAnnieEnabled = useFeatureFlag(FEATURE_FLAGS.Annie);
	// Gates the mobile menu entry. Mirrors the mount condition exactly, so the entry can never appear
	// without a panel behind it.
	const showMobileAnnie = isMobile && isAnnieEnabled && isHeadNurse(userInfo.role);
	// Same predicate the Staffing route switch forks on, so the bar and the screen can never disagree.
	const isGapsViewActive = useIsGapsViewActive();
	// Close only when Annie itself becomes unavailable (flag off, role change) — NOT when the layout
	// flips, which is just a rotation.
	const isAnnieAvailable = isAnnieEnabled && isHeadNurse(userInfo.role);
	useEffect(() => {
		if (!isAnnieAvailable) setIsAnnieOpen(false);
	}, [isAnnieAvailable]);
	// A walkthrough belongs to the department and the layout it was resolved for. Its route, its variant
	// branch and its anchors are all decided per-department, and the controller is desktop-only — so a
	// department switch makes it wrong, and a flip to mobile unmounts the only thing that could run it
	// while leaving it set. Coming back to desktop then hands a fresh controller a walkthrough its
	// `launchedRef` has never seen, which relaunches the stale tour from step one and re-navigates.
	// biome-ignore lint/correctness/useExhaustiveDependencies: the walkthrough is what's being discarded, not what triggers it.
	useEffect(() => {
		setActiveWalkthrough(null);
		// BOTH, or mobile Annie is concealed forever. `isWalkthroughRunning` is reported by the controller,
		// and going mobile UNMOUNTS it (it renders only on desktop) — so it never gets to report `false`,
		// and the flag it left behind hides the panel and the menu entry with nothing able to clear them.
		setIsWalkthroughRunning(false);
	}, [chosenDepartment.department_id, isMobile]);
	const isPbjCalculatorEnabled = useFeatureFlag(FEATURE_FLAGS.PbjCalculatorTab);
	const isPbjModelEnabled = useFeatureFlag(FEATURE_FLAGS.PbjModel);
	const isFacilitiesEnabled = useFeatureFlag(FEATURE_FLAGS.FacilitiesTab);
	// The Hiring tab shows for a hiring role (interviewer / recruiter / support), resolved by a backend call.
	// (The former `hiring_tab` feature flag was removed — access is role-based only.)
	const { data: hiringTabAccess } = useHubTabAccessQuery(true);
	// Some demo departments hide the tab for everyone, support included.
	const isHiringTabEnabled = (hiringTabAccess?.can_access ?? false) && !isHiringTabHiddenForDepartment(chosenDepartment.department_id);
	// Careers: `hiring_ihh` on for this department, no role check — the same rule as its route guard.
	const { allowed: isCareersTabEnabled } = useCareersAccess();
	useEffect(() => {
		if (userInfo?.role === NurseRole.CNA) {
			const feature_flag = tabs_to_feature_flag[currentPath as keyof typeof tabs_to_feature_flag];
			// The highlight is the header-tabs effect's below; this only sends a CNA off a tab they can't use.
			if (feature_flag !== undefined && feature_flag !== "") {
				for (const tab of tabs_priority) {
					const feature_flag = tabs_to_feature_flag[tab as keyof typeof tabs_to_feature_flag];
					if (feature_flag === "") {
						navigate(`/${chosenDepartment.department_id}${tab}`);
						break;
					}
				}
			}
		}
	}, [currentPath, userInfo, navigate, chosenDepartment]);

	const get_header_tabs = (): (keyof typeof tabs_name_map)[] => {
		const headerTabButtons: (keyof typeof tabs_name_map)[] = [];

		if (isShiftInsightsEnabled) {
			headerTabButtons.push("Shift Insights");
		}
		if (isStaffingEnabled) headerTabButtons.push("Update Staffing");
		if (isCommandCenterEnabled) headerTabButtons.push("Command Center");
		if (isTeamEnabled) headerTabButtons.push("Team");
		// Schedule Template, ROI Calculator, PBJ Model, Management Center, and Ops Backoffice
		// are SNF-oriented — hide them for hospitals.
		if (isMasterScheduleEnabled && !isHospital) headerTabButtons.push("Schedule Template");
		if (isManagementCenterEnabled && !isHospital) headerTabButtons.push("Management Center");
		if (isPbjCalculatorEnabled && !isHospital) headerTabButtons.push("ROI Calculator");
		if (isPbjModelEnabled && !isBedSideNurse(userInfo.role) && !isHospital) headerTabButtons.push("PBJ Model");
		if (isFacilitiesEnabled) headerTabButtons.push("Facilities");
		if (isAnyDemo && isHeadNurse(userInfo.role)) headerTabButtons.push("Patients");

		if (userInfo.support && !isHospital) {
			headerTabButtons.push("Ops Backoffice");
		}
		// Hiring sits last — after Ops Backoffice — per product ordering.
		if (isHiringTabEnabled) headerTabButtons.push("Hiring");
		// Careers is IHH's own applicant tracker — it sits beside Hiring, the other recruiting surface.
		if (isCareersTabEnabled) headerTabButtons.push("Careers");
		return headerTabButtons;
	};

	const get_enabled_header_tabs = (): (keyof typeof tabs_name_map)[] => {
		const headerTabButtons: (keyof typeof tabs_name_map)[] = [];
		if (isShiftInsightsEnabled) headerTabButtons.push("Shift Insights");
		if (isStaffingEnabled) headerTabButtons.push("Update Staffing");
		if (isCommandCenterEnabled) headerTabButtons.push("Command Center");
		if (isTeamEnabled) headerTabButtons.push("Team");
		if (isMasterScheduleEnabled && !isHospital) headerTabButtons.push("Schedule Template");
		if (isManagementCenterEnabled && !isHospital) headerTabButtons.push("Management Center");
		if (isPbjCalculatorEnabled && !isHospital) headerTabButtons.push("ROI Calculator");
		if (isPbjModelEnabled && !isBedSideNurse(userInfo.role) && !isHospital) headerTabButtons.push("PBJ Model");
		if (isFacilitiesEnabled) headerTabButtons.push("Facilities");
		if (isAnyDemo && isHeadNurse(userInfo.role)) headerTabButtons.push("Patients");
		if (userInfo.support && !isHospital) headerTabButtons.push("Ops Backoffice");
		if (isHiringTabEnabled) headerTabButtons.push("Hiring");
		if (isCareersTabEnabled) headerTabButtons.push("Careers");
		return headerTabButtons;
	};

	// `activeTab` is an INDEX into the tab list, and tabs gated on async checks (Hiring's access query,
	// Careers' flag) join the list after the first render. Re-sync when the list changes, not only on
	// navigation — otherwise landing on /careers highlights whichever tab later slid into its slot. A route no
	// tab owns highlights none (`NO_ACTIVE_TAB`), rather than leaving the last tab lit.
	const headerTabsKey = get_header_tabs().join("|");
	// biome-ignore lint/correctness/useExhaustiveDependencies: `headerTabsKey` stands in for the tab list, which `get_header_tabs` rebuilds every render.
	useEffect(() => {
		setActiveTab(get_header_tabs().findIndex((tabName) => isTabPath(tabName, currentPath)));
	}, [currentPath, headerTabsKey]);

	const changePath = (index: number) => {
		setActiveTab(index);
		const path = tabs_name_map[get_header_tabs()[index]];

		if (path && DEPARTMENT_INDEPENDENT_ROUTES.includes(path as (typeof DEPARTMENT_INDEPENDENT_ROUTES)[number])) {
			navigate(path);
		} else {
			navigate(`/${chosenDepartment.department_id}${path}`);
		}
	};
	const { updateUserInfo, saveTokenData } = useAuth();
	const changeDepartment = async (department: Department): Promise<void> => {
		try {
			// Get new access and refresh tokens for the new department. Carry the current view, or a
			// dual user in Admin view silently drops to Staff view on every department change — the
			// server re-validates it and can still refuse.
			const currentView = localStorageService.getChosenView();
			const params = new URLSearchParams({ department_id: department.department_id });
			if (currentView) params.set("view", currentView);
			const url = `/token/department?${params.toString()}`;
			let response: AxiosResponse<Token>;

			if (Capacitor.isNativePlatform()) {
				const { value: refreshToken, readFailed } = await readRefreshToken();
				if (!refreshToken) {
					trackEvent("department switch aborted", { reason: readFailed ? "read_failed" : "absent" });
					showSnackbar(t("error.department.switch.failed"), "error");
					return;
				}
				response = await axiosWrapper.post(url, { refresh_token: refreshToken });
			} else {
				// Web: use cookies (withCredentials is set in axiosWrapper)
				response = await axiosWrapper.post(url);
			}

			// Persist the choice and install the new tokens only after a successful exchange —
			// otherwise the stored department and axios headers would drift out of sync.
			localStorageService.setChosenDepartmentID(department.department_id);
			await saveTokenData(response.data);

			// Refresh user info to get updated departments list
			await updateUserInfo();

			// Keep the VIEW and the date the user is looking at — see `resolveDepartmentSwitchDestination`,
			// which owns the Staffing area's URL shapes so Monthly and People survive a switch too.
			navigate(resolveDepartmentSwitchDestination(location.pathname, department.department_id), { replace: true });
		} catch (error) {
			// Token exchange failed — do not navigate with a stale token; surface and stay put.
			console.error("Error getting new tokens for department:", error);
			showSnackbar(t("error.department.switch.failed"), "error");
		}
	};

	const headerRef = useRef<HTMLDivElement>(null);
	const [headerHeight, setHeaderHeight] = useState(0);
	useEffect(() => {
		/**
		 * Using ResizeObserver to monitor changes in the header's size and update the headerHeight state accordingly.
		 * This ensures that the content area below the header adjusts its height dynamically based on the header's height.
		 */
		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				if (entry.target === headerRef.current) {
					const h = entry.contentRect.height;
					setHeaderHeight(h);
					// Expose the live header height so any fixed-position child
					// (e.g. the staffing TopBar) can sit flush below it.
					document.documentElement.style.setProperty("--app-header-height", `${h}px`);
				}
			}
		});

		if (headerRef.current) {
			resizeObserver.observe(headerRef.current);
		}

		return () => {
			if (headerRef.current) {
				resizeObserver.unobserve(headerRef.current);
			}
		};
	}, []);

	return (
		// The provider wraps BOTH the routed page and the Annie panels, so any screen can hand Annie a
		// question ("why does this punch look wrong?") and the mounted panel picks it up.
		<AnnieAskProvider>
			{/* So a spotlit control can show itself at rest only while a tour on it runs (the Gaps Remove action). */}
			<WalkthroughRunningContext.Provider value={runningWalkthroughAnchors}>
				<PageContainer>
					<div
						className={Styles.container}
						// The bottom bar is fixed and portalled to `body`, so it costs this column no height and
						// would otherwise sit ON TOP of whatever the routed page put at its own bottom edge. Reserve
						// its clearance HERE rather than in each page: the bar is session-scoped and rides along to
						// every route, including the two it navigates to itself (`/requests`, `/post-shift`), which
						// have their own scroll containers and no reason to know the bar exists.
						style={isGapsViewActive ? { paddingBottom: BOTTOM_NAV.TOTAL_HEIGHT_CSS, boxSizing: "border-box" } : undefined}
					>
						<ErrorBoundary>
							<div
								ref={headerRef}
								style={{
									width: "100%",
									minHeight: "48px",
									height: "fit-content",
								}}
							>
								<ProductionEnvBanner />
								<Header
									setDepartmentListener={changeDepartment}
									departments={departmentsForView}
									tabs={get_header_tabs()}
									enabledTabs={get_enabled_header_tabs()}
									activeTab={activeTab}
									setActiveTab={changePath}
									onOpenAnnie={showMobileAnnie ? () => setIsAnnieOpen(true) : undefined}
								/>
							</div>
						</ErrorBoundary>
						<div
							style={{
								// Grow to fill the space below the header. Flex (not a fixed calc) sizes it to the
								// header's actual rendered height.
								flex: 1,
								minHeight: 0,
								width: "100%",
							}}
						>
							<ContentWrapper>
								{/**
								 * The <Outlet /> component is what renders the matched child route inside Layout.
								 * Without it, the nested routes would never appear - you'd just see the header with an empty content area.
								 */}
								<ErrorBoundary>{<Outlet />}</ErrorBoundary>
							</ContentWrapper>
						</div>
						{/* The gaps-first bottom bar belongs to the SESSION, not to one route: it stands in for the
						    hamburger for as long as the user is in that view, so it has to outlive every navigation
						    it offers. Mounted on the Staffing page instead, it vanished the moment you tapped one of
						    its own slots. Its clearance is paid by this column's padding above. */}
						{isGapsViewActive && <GapsBottomNav onOpenAnnie={showMobileAnnie ? () => setIsAnnieOpen(true) : undefined} />}
					</div>
					{/* Annie replaces the legacy user-guide chat when its flag is on — never two launchers.
			    Both launchers are hidden on the Hiring page — the floating FAB overlaps the chat composer's send button. */}
					{!isMobile && isHeadNurse(userInfo.role) && isUserGuideChatEnabled && !isAnnieEnabled && currentPath !== tabs_name_map.Hiring && (
						<UserGuideChatWidget />
					)}
					{/* ONE instance for both layouts. Two instances gated on `isMobile` meant crossing the 768px
				    threshold — i.e. rotating a phone to landscape — unmounted the panel and destroyed the
				    transcript and any in-flight stream. The FAB is desktop-only; mobile opens from the menu.
				    Boundaried like the walkthrough below it: the header and the <Outlet /> have their own, but
				    both are SIBLINGS of this — nothing wraps Layout itself (App.tsx mounts it bare), so an
				    uncaught throw in the panel would take the header, page and footer down with it. */}
					{isAnnieAvailable && (
						<ErrorBoundary>
							<AnniePanel
								open={isAnnieOpen}
								onOpenChange={setIsAnnieOpen}
								// No chip where no tour can run. The backend already withholds the offer for a mobile
								// surface, so this covers the viewport flip: answered on desktop, tapped after a rotate.
								onStartWalkthrough={isMobile ? undefined : setActiveWalkthrough}
								// The tour owns the screen while it runs. Annie is not closed — `open` stays the
								// user's intent, so she comes back on her own when the tour ends.
								concealed={isWalkthroughRunning}
								showLauncher={!isMobile && currentPath !== tabs_name_map.Hiring}
							/>
						</ErrorBoundary>
					)}
					{/* Here, not inside the panel: a host inside the routed content would unmount itself on its own
				    first step. Inside an ErrorBoundary because the tour library is dynamically imported, and a
				    chunk that fails to fetch after a deploy would otherwise take the layout down. */}
					{isAnnieAvailable && !isMobile && (
						<ErrorBoundary>
							<AnnieWalkthroughController walkthrough={activeWalkthrough} onFinish={clearWalkthrough} onRunningChange={setIsWalkthroughRunning} />
						</ErrorBoundary>
					)}
				</PageContainer>
			</WalkthroughRunningContext.Provider>
		</AnnieAskProvider>
	);
}
